import React, { MouseEventHandler, forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { IFundSection } from "./SecondaryNav.types";
import { useInViewObserver } from "./useInViewObserver";
import "./secondaryNav.scss";
import { jumpToSection } from "./jumpToSection";
import { getCleanPageURL } from "./setSecondaryNavState";
import HTMLReactParser from "html-react-parser";
import { createPortal } from 'react-dom';

export interface ISecondaryNav extends ComponentInterface { }

//Handle bots
const isCrawler = /bot|googlebot|crawler|spider|robot|crawling/i.test(navigator.userAgent);
//Whether or not to render <a> tags around secondary nav items. Might be needed for Crawlers.
const addLinksToSecondaryNav = false;

export interface HeaderHeightEvent extends Event {
    detail?: {
        headerHeight: number | undefined,
        cumilativeAlertBarsHeight: number | undefined
    }
}

const headerHeightEventHandler = (e: HeaderHeightEvent) => {
    if (!e.detail) return;
    let secondayNavOffsetTop = (e.detail.headerHeight ?? 0) + (e.detail.cumilativeAlertBarsHeight ?? 0);
    return secondayNavOffsetTop;
}

//For when the fund launch promo-banner is there on the page.
const stickySecondaryNavScrollHandler = (e: Event, setFixedClass: React.Dispatch<React.SetStateAction<string>>) => {
    const fundPageBody = document.querySelector(".fund-details-body-wrp");
    const stickeyHeaderHeight = document.querySelector("#sticky-site-header")?.getBoundingClientRect().height ?? 0;

    //see if fundpage body is under the sticky header.
    if (fundPageBody && fundPageBody.getBoundingClientRect().top < stickeyHeaderHeight + 75) {
        setFixedClass("");
    } else {
        setFixedClass("unfixed");
    }
}

export default function SecondaryNav(props: ISecondaryNav) {
    const element = props.rootElement;
    element.classList.remove("d-none");
    const [fixedClass, setFixedClass] = useState("");
    const [FundSections, setFundSections] = useState(getFundSections());
    const cleanURL = getCleanPageURL(FundSections);
    const [topOffset, setTopOffset] = useState(0);
    const stickyHeaderRef = useRef<HTMLDivElement>(null);
    const sidebarRef = useRef<HTMLElement>(null);
    const placeholderRef = useRef<HTMLDivElement>(null);
    const isStuckRef = useRef(false);
    
    useEffect(() => {
      const updateStickyTop = () => {
        if (stickyHeaderRef.current) {
          const rect = stickyHeaderRef.current.getBoundingClientRect();
          const headerHeight = rect.height;
          
          document.documentElement.style.setProperty('--sticky-header-height', `${headerHeight}px`);
        }
      };
      
      const resizeObserver = new ResizeObserver(updateStickyTop);
      
      if (stickyHeaderRef.current) {
        resizeObserver.observe(stickyHeaderRef.current);
      }
      
      updateStickyTop();

      let ticking = false;
      const onScrollResize = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            updateStickyTop();
            ticking = false;
          });
          ticking = true;
        }
      };

      window.addEventListener('resize', onScrollResize);
      window.addEventListener('scroll', onScrollResize);

      return () => {
        resizeObserver.disconnect();
        window.removeEventListener('resize', onScrollResize);
        window.removeEventListener('scroll', onScrollResize);
      };
    }, []);
    
    useEffect(() => {
        const checkIfStuck = () => {
            const sidebar = sidebarRef.current;
            const placeholder = placeholderRef.current;
            const stickyHeader = document.querySelector('.sticky-header');
            
            if (sidebar && placeholder && stickyHeader) {
                const headerRect = stickyHeader.getBoundingClientRect();
                
                const elementToCheck = isStuckRef.current ? placeholder : sidebar;
                const elementRect = elementToCheck.getBoundingClientRect();
                
                const shouldBeFixed = elementRect.top <= headerRect.bottom;
                
                if (shouldBeFixed && !isStuckRef.current) {
                    placeholder.style.height = `${sidebar.offsetHeight}px`;
                    placeholder.style.display = 'block';
                    sidebar.classList.add('fixed');
                    isStuckRef.current = true;
                } else if (!shouldBeFixed && isStuckRef.current) {
                    placeholder.style.display = 'none';
                    sidebar.classList.remove('fixed');
                    isStuckRef.current = false;
                }
            }
        };
        
        let ticking = false;
        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    checkIfStuck();
                    ticking = false;
                });
                ticking = true;
            }
        };
        
        checkIfStuck();
        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', checkIfStuck);
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', checkIfStuck);
        };
    }, []);
    
    useEffect(() => {
        const titleWrapperDesktop = document.querySelector('.fund-title-wrapper-desktop');
        const titleWrapperMobile = document.querySelector('.fund-title-wrapper-mobile');

        const navWrapper = $('.fund-page-secondary-nav-wrp');
        const navInner = $(".fund-page-secondary-nav-wrp > div");

        const options = {
            root: null,
            threshold: [0.0, 0.5, 1.0] 
        };

        const handleIntersection = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
            const isAnyTitleVisibleEnough = entries.some(entry => {
                return entry.intersectionRatio >= 0.5;
            });

            if (isAnyTitleVisibleEnough) {
                navWrapper.removeClass("fixed-nav");
                navInner.removeClass("container");
            } else {
                navWrapper.addClass("fixed-nav");
                navInner.addClass("container");
            }
        };

        const observer = new IntersectionObserver(handleIntersection, options);

        if (titleWrapperDesktop) {
            observer.observe(titleWrapperDesktop);
        }
        if (titleWrapperMobile) {
            observer.observe(titleWrapperMobile);
        }

        return () => {
            if (titleWrapperDesktop) {
                observer.unobserve(titleWrapperDesktop);
            }
            if (titleWrapperMobile) {
                observer.unobserve(titleWrapperMobile);
            }
        };
    }, []);
    
    useSectionJumpOnURL(FundSections);
    useInViewObserver(FundSections, setFundSections);
    
    useEffect(() => {
        const activeItem = document.querySelector('.sidebar-nav__item.active');
        
        if (activeItem) {
            activeItem.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center'
            });
        }
    }, [FundSections]);

    useEffect(() => {
        const handler = (e: Event) => {
            let offSet = headerHeightEventHandler(e as HeaderHeightEvent) ?? topOffset;
            offSet += 50;
            setTopOffset(offSet);
            document.documentElement.style.setProperty('--secondary-nav-offset', offSet + 'px');
        };
        document.addEventListener("headerHeightEvent", handler);
        return () => {
            document.removeEventListener("headerHeightEvent", handler);
        };
    }, []);
    
    useEffect(() => {
        if (!document.querySelector(".fund-banner-wrapper .desktop-content")) return;
        setFixedClass("unfixed");
        const scrollHandler = (e: Event) => stickySecondaryNavScrollHandler(e, setFixedClass);
        window.addEventListener("scroll", scrollHandler);
        return () => {
            window.removeEventListener("scroll", scrollHandler);
        }
    }, [])
    
    useEffect(() => {
        if (element?.parentElement) {
            element.parentElement.style.minHeight = '0';
        }
    }, [element]);

    useEffect(() => {
        let ticking = false;
        const handleScroll = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
                const openDropdown = document.querySelector('.research_links__mobile.dropdown.show');
                if (openDropdown) {
                    const toggle = openDropdown.querySelector('[data-toggle="dropdown"]');
                    if (toggle && window.$) {
                        window.$(toggle).dropdown('hide');
                    } else {
                        openDropdown.classList.remove('show');
                        openDropdown.querySelector('.dropdown-menu')?.classList.remove('show');
                    }
                }
                ticking = false;
            });
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <>
            <StickyHeader ref={stickyHeaderRef} FundSections={FundSections}/>
            <div ref={placeholderRef} style={{ display: 'none' }} />
            <aside
                ref={sidebarRef}
                className={`sticky-left-sidebar ${fixedClass} nav-trans`}
                aria-label="Jump to"
            >
                <nav className="sidebar-nav-wrapper">
                    <ul className="sidebar-nav container">
                        {FundSections &&
                            FundSections.map((FundSection) => (
                                <SecondaryNavItem
                                    key={FundSection.id as React.Key}
                                    FundSection={FundSection}
                                    displayClass="sidebar-nav__item"
                                    cleanURL={cleanURL}
                                ></SecondaryNavItem>
                            ))}
                    </ul>
                </nav>
            </aside>
        </>
    );
}

function initCopyTooltips(scope?: Element | Document) {
    const root = scope || document;
    if (!window.$) return;

    root.querySelectorAll(".fund-details-header-section__img").forEach((el) => {
        const $el = window.$(el);
        
        if ($el.data("copy-tooltip-init")) return;
        $el.data("copy-tooltip-init", true);

        const originalTitle = $el.attr("title");
        $el.tooltip();

        $el.on("click", () => {
            const copiedText = $el.attr("data-copiedtext") || "Copied";
            $el.tooltip("dispose");
            $el.attr("title", copiedText);
            $el.tooltip();
            $el.tooltip("show");
            navigator.clipboard.writeText($el.attr("data-value"));
            setTimeout(() => {
                $el.tooltip("dispose");
                $el.attr("title", originalTitle);
                $el.tooltip();
            }, 1000);
        });
    });
}

const StickyHeader = forwardRef<HTMLDivElement, { FundSections: IFundSection[] }>(
  ({ FundSections }, ref) => {
    const ticker = useMemo(() => document.querySelector(".fund-title-wrapper div.ticker")?.innerHTML ?? "", []);
    const fundName = useMemo(() => document.getElementsByTagName("ve-fundname")[0]?.textContent?.trim() ?? "", []);
    const activeSectionLabel = FundSections.find((x) => x.activeClass == "active")?.name ?? "";
    const [secondaryNavTop, setsecondaryNavTop] = useState(0);
    const cleanURL = getCleanPageURL(FundSections);
    const isMutualFund = useMemo(() => document.getElementsByClassName("mutual-fund-page")[0] ? true : false, []);
    const [researchLinksHTML, setResearchLinksHTML] = useState<string>("");
    const [overviewLinksHTML, setOverviewLinksHTML] = useState<string>("");
    const [fundDetailsHTML, setFundDetailsHTML] = useState<string>("");

    /**
     * The alertbar code will dispatch an event to document whenever the alertbars are added or removed from the page.
     * We need to listen to these events and adjust the offset of our sticky nav accordingly.
     * Refer generic-promo-banner.js for details of dispatcher.
     */

    useEffect(() => {
        const defaultHeaderHeight = document.querySelector(".root .sticky-wrapper")?.getBoundingClientRect().height ?? 0
        setsecondaryNavTop(defaultHeaderHeight);
        const setNavHeight = (e: HeaderHeightEvent) => {
            let offSet = headerHeightEventHandler(e) ?? secondaryNavTop;
            setsecondaryNavTop(offSet);
        }
        document.addEventListener("headerHeightEvent", setNavHeight);
        
        return () => document.removeEventListener("headerHeightEvent", setNavHeight);
    }, []);
    
    useEffect(() => {
        const researchLinks = document.querySelector(".research_links");
        if (researchLinks) {
            setResearchLinksHTML(researchLinks.parentElement?.innerHTML ?? "");
        }

        const overviewSection = document.querySelector(".overview-link-section .content-area-right-bottom-part");
        if (overviewSection) {
            const clonedSection = overviewSection.cloneNode(true) as HTMLElement;
            const scripts = clonedSection.querySelectorAll("script");
            scripts.forEach(script => script.remove());
            setOverviewLinksHTML(clonedSection.innerHTML);
        }

        const fundDetailsSection = document.querySelector(".fund-details-header-section");
        if (fundDetailsSection) {
            const cloned = fundDetailsSection.cloneNode(true) as HTMLElement;
            cloned.querySelectorAll("script").forEach(s => s.remove());
            setFundDetailsHTML(cloned.innerHTML);
        }
    }, []);

    useEffect(() => {
        if (!fundDetailsHTML) return;
        initCopyTooltips();
    }, [fundDetailsHTML]);

    return (
        <div ref={ref} className="sticky-header" style={{ top: 'calc(var(--header-height) + var(--alert-bars-height))' }}>
            <div className="container">
                <div className="inner-container">
                    <div>
                        <FundLabelContent ticker={ticker} fundName={fundName} />
                        {fundDetailsHTML && (
                            <div className="fund-details-header-section__sticky">
                                {HTMLReactParser(fundDetailsHTML)}
                            </div>
                        )}
                    </div>
                </div>
                <div className="inner-container-mobile">
                    <FundLabelContent ticker={ticker} fundName={fundName} isMutualFund={isMutualFund} />
                    {fundDetailsHTML && (
                        <div className="fund-details-header-section__sticky">
                            {HTMLReactParser(fundDetailsHTML)}
                        </div>
                    )}
                    <div className="mobile-secondary-nav-wrapper">
                        <MobileButtonAndDropdown FundSections={FundSections} cleanURL={cleanURL} activeSectionLabel={activeSectionLabel as string} />
                        {overviewLinksHTML && (
                            <div className="overview_links__wrapper" onClick={(e) => handleOverviewLinksClick(e)}>
                                {HTMLReactParser(overviewLinksHTML)}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});

//declare jQuery bootstrap;
declare global {
    interface Window { $: Function | any }
}

function FundLabelContent({ ticker, fundName, isMutualFund }: { ticker: string; fundName: string, isMutualFund?: boolean }) {
    var dropdown = document.getElementsByClassName("mf-fund-selector")[0];
    useEffect(()=>{
        if(dropdown){
            var wrapperDiv = document.getElementsByClassName("mf-dropdown-div")[0];
            wrapperDiv.innerHTML = dropdown.outerHTML
        }
    },[])
    if(isMutualFund){
        return(
            <div className="fund-label d-flex align-items-center my-2">
                <div className="fund-name-label px-2">{fundName}</div>
                <div className="ticker-label px-2 font-weight-bold">{HTMLReactParser(ticker)}</div>
                <div className="mf-dropdown-div"></div>
            </div>
        )
    }
    else return (
        <div className="fund-label d-flex">
            <div className="ticker-label pr-2 font-weight-bold">{HTMLReactParser(ticker)}</div>
            <div className="fund-name-label px-2">{fundName} </div>
        </div>
    );
}

export function Breadcrumbs() {
    const pageBreadcrumb = document.querySelectorAll(".fund-breadcrumb-wrp");
    if (pageBreadcrumb.length === 0) return <></>;
    const breadcrumbMarkup = pageBreadcrumb[0].innerHTML;
    return <div className="fund-breadcrumb-wrp laptop-only">{HTMLReactParser(breadcrumbMarkup)}</div>
}

export function getFundSections(): IFundSection[] {
    const FundPageSectionNodes = document.querySelectorAll("section.fund-page-section");
    return Array.prototype.map.call(FundPageSectionNodes, function (sectionNode: Element): IFundSection {
        return {
            id: sectionNode.id,
            name: sectionNode.querySelector("h2.fund-page-section-header")?.getAttribute("data-section-name") ?? null,
            activeClass: "",
        };
    }) as IFundSection[];
}

const clickListenerForSeondaryNavAnchors: MouseEventHandler<HTMLAnchorElement> = (e) => !isCrawler && e.preventDefault();

function SecondaryNavItem({ FundSection, displayClass, cleanURL }: { FundSection: IFundSection; displayClass: string, cleanURL: string }) {
    const { name, id, activeClass } = FundSection;
    const url = `${cleanURL}/${id}/`;

    return (
        <li className={displayClass + " " + activeClass} data-target={id} onClick={handleClick}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" width="16" height="16" fill="currentColor"><path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"></path></svg>
            {addLinksToSecondaryNav ?
                <a href={url} onClick={clickListenerForSeondaryNavAnchors} className="secondary-nav-link">{name}</a> : <><span className="secondary-nav-link">{name}</span></>}
        </li>
    );
}

function handleClick(event: React.MouseEvent<HTMLElement>) {
    if(isCrawler) return true;
    const currentTarget = event.currentTarget;
    const targetedElementId = currentTarget.dataset.target ?? "";
    jumpToSection(targetedElementId);
}

function handleOverviewLinksClick(e: React.MouseEvent<HTMLDivElement>) {
    const target = e.target as HTMLElement;
    const link = target.closest("a");
    
    if (!link) return;

    if (link.classList.contains("id-how-to-buy-etf-modal")) {
        e.preventDefault();
        
        const modal = document.getElementById("id-how-to-buy-etf-modal");
        if (modal && window.$) {
            window.$(modal).modal("show");
        }
    }
}


function useSectionJumpOnURL(FundSections: IFundSection[]) {
    useEffect(() => {
        try {
            const urlPath = window.location.pathname.split("/").reverse();
            const lastPathToken = urlPath[0] == "" ? urlPath[1] : urlPath[0];
            const targetSection = FundSections.find((x) => x.id == lastPathToken);
            const targetSectionId = targetSection?.id;
            if (!targetSectionId) return;
            if (targetSection == FundSections[0]) return;
            window.setTimeout(() => {
                window.scrollY = 0;
                jumpToSection(targetSectionId as string)
            }, 600);
        } catch (error) {
            console.log(error);
        }
    }, []);
}

function MobileButtonAndDropdown({ FundSections, cleanURL, activeSectionLabel }: { FundSections: IFundSection[], cleanURL: string, activeSectionLabel: string }) {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || !window.$) return;

        const getToggle = () => container.querySelector('[data-toggle="dropdown"]') as HTMLElement | null;
        const getMenu = () => container.querySelector('.dropdown-menu') as HTMLElement | null;

        // Pin the menu to the visual viewport with position:fixed and an explicit max-height.
        // Avoids the absolute-positioning-inside-fixed-ancestor scroll quirks on Android Chrome
        // and decouples our height from any CSS-variable cascade.
        const updateMenuPosition = () => {
            const toggle = getToggle();
            const menu = getMenu();
            if (!toggle || !menu) return;
            const vv = window.visualViewport;
            const viewportHeight = vv ? vv.height : window.innerHeight;
            const vpOffsetTop = vv ? vv.offsetTop : 0;
            const vpOffsetLeft = vv ? vv.offsetLeft : 0;
            const rect = toggle.getBoundingClientRect();
            const top = rect.bottom - vpOffsetTop;
            const available = Math.max(viewportHeight - top - 8, 50);

            menu.style.position = 'fixed';
            menu.style.top = `${top}px`;
            menu.style.left = `${rect.left - vpOffsetLeft}px`;
            menu.style.width = `${rect.width}px`;
            // Override CSS `min-width: 100%`: once the menu becomes position:fixed,
            // 100% resolves against the viewport instead of the toggle's parent,
            // and it would render much wider than the button.
            menu.style.minWidth = `${rect.width}px`;
            menu.style.maxWidth = `${rect.width}px`;
            menu.style.maxHeight = `${available}px`;
            menu.style.overflowY = 'auto';
            menu.style.setProperty('-webkit-overflow-scrolling', 'touch');
            menu.style.overscrollBehavior = 'contain';
        };

        const clearMenuPosition = () => {
            const menu = getMenu();
            if (!menu) return;
            menu.style.position = '';
            menu.style.top = '';
            menu.style.left = '';
            menu.style.width = '';
            menu.style.minWidth = '';
            menu.style.maxWidth = '';
            menu.style.maxHeight = '';
            menu.style.overflowY = '';
            menu.style.removeProperty('-webkit-overflow-scrolling');
            menu.style.overscrollBehavior = '';
        };

        const handleShow = () => {
            // Wait two frames so Bootstrap/Popper finishes positioning before we override it,
            // and so the Android address-bar collapse has settled before we measure.
            requestAnimationFrame(() => requestAnimationFrame(updateMenuPosition));
            window.addEventListener('resize', updateMenuPosition);
            window.addEventListener('orientationchange', updateMenuPosition);
            window.addEventListener('scroll', updateMenuPosition);
            window.visualViewport?.addEventListener('resize', updateMenuPosition);
            window.visualViewport?.addEventListener('scroll', updateMenuPosition);
        };

        const handleHide = () => {
            clearMenuPosition();
            window.removeEventListener('resize', updateMenuPosition);
            window.removeEventListener('orientationchange', updateMenuPosition);
            window.removeEventListener('scroll', updateMenuPosition);
            window.visualViewport?.removeEventListener('resize', updateMenuPosition);
            window.visualViewport?.removeEventListener('scroll', updateMenuPosition);
        };

        window.$(container).on('shown.bs.dropdown', handleShow);
        window.$(container).on('hide.bs.dropdown', handleHide);
        return () => {
            window.$(container).off('shown.bs.dropdown', handleShow);
            window.$(container).off('hide.bs.dropdown', handleHide);
            clearMenuPosition();
            window.removeEventListener('resize', updateMenuPosition);
            window.removeEventListener('orientationchange', updateMenuPosition);
            window.removeEventListener('scroll', updateMenuPosition);
            window.visualViewport?.removeEventListener('resize', updateMenuPosition);
            window.visualViewport?.removeEventListener('scroll', updateMenuPosition);
        };
    }, []);

    return (<div ref={containerRef} className="mobile-secondary-nav dropdown align-items-center">
        <DesktopDropdownButton buttonClass={null} buttonText={activeSectionLabel as string} isMobile={true} />
        <nav className="dropdown-menu dropdown-menu-right" aria-label="Jump to">
            {FundSections &&
                FundSections.map((FundSection) => (
                    <SecondaryNavItem
                        key={FundSection.id as React.Key}
                        FundSection={FundSection}
                        displayClass="dropdown-item text-util-md"
                        cleanURL={cleanURL}
                    ></SecondaryNavItem>
                ))}
        </nav>
    </div>)
}

const DesktopDropdownButton = function (props: {
    buttonText: string | null;
    buttonClass: string | null;
    isMobile?: Boolean | false;
}) {
    const { buttonText, buttonClass, isMobile } = props;
    const text = buttonText ?? "";
    const classText = buttonClass ?? "dropdown__button-simple-toggle";

    return (
        <>
            {(text != "" || !isMobile) && (
                <button
                    type="button"
                    aria-haspopup="true"
                    aria-expanded="false"
                    className={`dropdown__button ${classText} dropdown__button-default`}
                    data-toggle="dropdown"
                >
                    {text != "" && <span className="text-util-md dropdown-label">{text}</span>}
                    <span className="dropdown__icon-bg">
                        <svg
                            aria-hidden="true"
                            focusable="false"
                            data-prefix="far"
                            data-icon="chevron-down"
                            className="svg-inline--fa fa-chevron-down fa-w-16 fa-2x dropdown__icon dropdown__icon-default"
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 18 18"
                        >
                            <path
                                fill="currentColor"
                                d="M2.26 4.88a.75.75 0 00-.54.21.75.75 0 000 1.07l6.75 6.75a.75.75 0 001.06 0l6.75-6.75a.75.75 0 000-1.07.75.75 0 00-1.06 0L9 11.31 2.78 5.1a.75.75 0 00-.52-.21z"
                            ></path>
                        </svg>
                    </span>
                </button>
            )}
        </>
    );
};